import { useState, useRef, useEffect, useMemo } from "react";
import { useBatchLoader } from "./hooks/useBatchLoader";
import { useClientStore } from "./hooks/useClientStore";
import { useHoldingsStore } from "./hooks/useHoldingsStore";
import { useActivityLog, migrateLegacyNotes } from "./hooks/useActivityLog";
import { useIsMobile } from "./hooks/useIsMobile";
import { normalizeId } from "./utils/formatters";
import ClientDashboard from "./components/ClientDashboard";
import PortalApp from "./portal/PortalApp";
import PlanViewer from "./components/PlanViewer";
import ClientSidebar from "./components/ClientSidebar";
import MobileDrawer from "./components/MobileDrawer";
import ActivityLogPanel from "./components/ActivityLogPanel";
import UploadModal from "./components/UploadModal";
import HomeDashboard from "./components/HomeDashboard";
import BulkReportSendModal from "./components/BulkReportSendModal";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./components/LoginPage";
import { hachsharaTrackMap } from "./parsers/hachsharaParser";
import SubscriptionGate from "./components/SubscriptionGate";
import PricingPage from "./components/PricingPage";
import AdminDashboard from "./components/AdminDashboard";
import StorageSettingsModal from "./components/StorageSettingsModal";
import DeviceBlockedModal from "./components/DeviceBlockedModal";
import HachsharaImport from "./components/HachsharaImport";
import MigdalImport from "./components/MigdalImport";
import HarelImport from "./components/HarelImport";
import ClalImport from "./components/ClalImport";
import { isClalExe } from "./parsers/clalArchive";
import { useClalHealthStore } from "./hooks/useClalHealthStore";
import ManagedPortfolioImport from "./components/ManagedPortfolioImport";
import { useHachsharaStore } from "./hooks/useHachsharaStore";
import { useHarelStore } from "./hooks/useHarelStore";
import { useMenoraStore } from "./hooks/useMenoraStore";
import MenoraImport from "./components/MenoraImport";
import AltshulerImport from "./components/AltshulerImport";
import FilesManagerModal from "./components/FilesManagerModal";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import { memoryStore } from "./utils/storage";
import { SOURCE_TYPE } from "./utils/dataSource";
import "./App.css";

function UploadScreen({ onFiles, status, onOpenFilesManager }) {
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = e.dataTransfer.files;
    if (files?.length) onFiles(files);
  };

  return (
    <div className="upload-screen">
      <div className="upload-header">
        <div className="upload-header-icon">🏦</div>
        <h1 className="upload-header-title">מסלקה פנסיונית</h1>
        <p className="upload-header-subtitle">מערכת ניתוח קבצי מבנה אחיד 008</p>
      </div>

      <div className="upload-container">
        <div
          className={`drop-zone ${dragging ? "dragging" : ""}`}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById("uploadFileInput").click()}
        >
          <div style={{ fontSize: 56, marginBottom: 16 }}>📂</div>
          <h2 style={{ fontSize: 20, color: "#334155", margin: "0 0 8px" }}>
            גרור קובץ ZIP / DAT לכאן
          </h2>
          <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>או לחץ לבחירת קבצים</p>
          <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>
            תומך בקבצי ZIP מהמסלקה וקבצי DAT מהפניקס · ניתן לבחור כמה יחד
          </p>
          <input
            id="uploadFileInput"
            type="file"
            accept=".zip,.dat,.xml"
            multiple
            style={{ display: "none" }}
            onChange={e => { if (e.target.files?.length) onFiles(e.target.files); }}
          />
        </div>

        {status && (
          <div className={`status-indicator ${status.type}`}>
            {status.type === "loading" && <span>⏳ </span>}
            {status.type === "success" && <span>✅ </span>}
            {status.type === "error" && <span>❌ </span>}
            {status.msg}
          </div>
        )}

        <div style={{ marginTop: 24, textAlign: "center", display: "flex", gap: 12, justifyContent: "center" }}>
          <button
            onClick={() => document.getElementById("importBackupInitialInput").click()}
            style={{
              background: "transparent",
              color: "#4338ca",
              border: "1px solid #4338ca",
              padding: "8px 16px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14
            }}
          >
            📂 שחזור נתונים
          </button>
          <button
            onClick={onOpenFilesManager}
            style={{
              background: "transparent",
              color: "#059669",
              border: "1px solid #059669",
              padding: "8px 16px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14
            }}
          >
            🗂️ ניהול קבצים
          </button>
          <input 
            id="importBackupInitialInput"
            type="file" 
            accept=".pension,.json" 
            style={{ display: "none" }} 
            onChange={async (e) => {
              if (!e.target.files[0]) return;
              const { importWorkspace } = await import("./utils/storage");
              try {
                await importWorkspace(e.target.files[0]);
                alert("השחזור הושלם בהצלחה! האפליקציה תתרענן כעת.");
                window.location.reload();
              } catch (err) {
                alert("שגיאה בטעינת קובץ הגיבוי.");
              }
              e.target.value = "";
            }} 
          />
        </div>

      </div>
    </div>
  );
}

function SyncErrorBadge() {
  const { syncStatus, syncError, retrySync } = useAuth();
  const [busy, setBusy] = useState(false);
  const handleRetry = async () => {
    setBusy(true);
    try { await retrySync(); } finally { setBusy(false); }
  };
  if (syncStatus === 'syncing') return (
    <div style={{
      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white', border: '1px solid #60a5fa', borderRadius: 12,
      padding: '10px 18px', fontSize: 13, fontWeight: 700,
      boxShadow: '0 0 15px rgba(59, 130, 246, 0.6)', display: 'flex', alignItems: 'center', gap: 8,
      animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite'
    }}>
      <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⟳</span>
      <span>מסנכרן עם הענן…</span>
    </div>
  );
  if (syncStatus === 'error') return (
    <button
      onClick={handleRetry}
      disabled={busy}
      title={syncError || 'שגיאת סנכרון'}
      style={{
        background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 8,
        padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
        boxShadow: '0 2px 8px rgba(220, 38, 38, 0.25)', display: 'flex', alignItems: 'center', gap: 6,
      }}
    >
      <span>⚠️</span>
      <span>{busy ? 'מסנכרן…' : 'הסנכרון לענן נכשל — לחץ לנסיון חוזר'}</span>
    </button>
  );
  return null;
}

function SignOutButton({ email, onSignOut, isAdmin, onAdmin, onStorageSettings }) {
  return (
    <div style={{ position: 'fixed', bottom: 20, left: 20, zIndex: 1000, display: 'flex', gap: 8, alignItems: 'center' }}>
      <SyncErrorBadge />
      {isAdmin && (
        <button onClick={onAdmin} style={{
          background: '#1e293b', color: 'white', border: 'none', borderRadius: 8,
          padding: '8px 14px', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>⚙️ Admin</button>
      )}
      <button onClick={onStorageSettings} title="הגדרות אחסון" style={{
        background: 'white', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 8,
        padding: '8px 14px', fontSize: 12, fontWeight: 500,
        cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>&#9729; אחסון</button>
      <button onClick={onSignOut} title={email} style={{
        background: 'white', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 8,
        padding: '8px 14px', fontSize: 12, fontWeight: 500,
        cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>התנתקות</button>
    </div>
  );
}

function FullPageSpinner({ message = 'טוען' }) {
  const cleanMsg = message.replace(/[.…]+$/, '');
  return (
    <>
      <style>{`
        @keyframes plPulse {
          0%,100% { transform: scale(1);    opacity: 1;   }
          50%      { transform: scale(1.13); opacity: .82; }
        }
        @keyframes plBar {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(380%);  }
        }
        @keyframes plDot {
          0%,80%,100% { transform: translateY(0);   opacity: .3; }
          40%         { transform: translateY(-6px); opacity: 1;  }
        }
        @keyframes plFade {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .pl-dot { display:inline-block; animation: plDot 1.35s ease-in-out infinite; }
        .pl-dot:nth-child(1) { animation-delay: 0s;    }
        .pl-dot:nth-child(2) { animation-delay: .2s;   }
        .pl-dot:nth-child(3) { animation-delay: .4s;   }
      `}</style>
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(150deg, #f1f5f9 0%, #e9eef8 100%)',
        direction: 'rtl',
      }}>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22,
          animation: 'plFade .45s ease-out both',
        }}>
          {/* לוגו */}
          <div style={{
            animation: 'plPulse 2.4s ease-in-out infinite',
            filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.1))',
            lineHeight: 1,
            marginBottom: 8,
          }}>
            <img src="/mivida-logo.png" alt="MIVIDA" style={{ height: 64, objectFit: 'contain' }} />
          </div>

          {/* סלוגן */}
          <div style={{ fontSize: 13, color: '#94a3b8', letterSpacing: '.2px', fontWeight: 500 }}>
            מערכת הפרישה הקלה והטובה בישראל
          </div>

          {/* טקסט + נקודות קופצות */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b' }}>
            <span>{cleanMsg}</span>
            <span style={{ display:'flex', gap: 3, marginRight: 2 }}>
              <span className="pl-dot" style={{ fontSize: 16, lineHeight: 1 }}>·</span>
              <span className="pl-dot" style={{ fontSize: 16, lineHeight: 1 }}>·</span>
              <span className="pl-dot" style={{ fontSize: 16, lineHeight: 1 }}>·</span>
            </span>
          </div>

          {/* פס התקדמות */}
          <div style={{
            width: 220, height: 3, background: '#dde4f0', borderRadius: 99, overflow: 'hidden',
          }}>
            <div style={{
              width: '38%', height: '100%', borderRadius: 99,
              background: 'linear-gradient(90deg, #818cf8, #4338ca)',
              animation: 'plBar 1.7s cubic-bezier(.4,0,.2,1) infinite',
            }} />
          </div>
        </div>
      </div>
    </>
  );
}

function AppContent() {
  // Portal route — fully self-contained, no advisor hooks needed
  if (window.location.pathname.startsWith('/portal')) return <PortalApp />;

  // Plan share route — public read-only plan viewer, no advisor login
  if (window.location.pathname.startsWith('/plan')) return <PlanViewer />;

  const { loading, session, isExpired, isAdmin, signOut, deviceBlock, retryDevice, initialSyncDone, hasHoldingsFeature, syncStatus } = useAuth();
  const [showAdmin, setShowAdmin] = useState(false);
  const [showStorageSettings, setShowStorageSettings] = useState(false);
  const [view, setView] = useState(null); // null | 'pricing'
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const [draggingFile, setDraggingFile] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [logPanelClient, setLogPanelClient] = useState("הכל");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showHachsharaImport, setShowHachsharaImport] = useState(false);
  const [hachsharaFiles, setHachsharaFiles] = useState(null);
  const [showMigdalImport, setShowMigdalImport] = useState(false);
  const [migdalFiles, setMigdalFiles] = useState(null);
  const [showHarelImport, setShowHarelImport] = useState(false);
  const [harelFiles, setHarelFiles] = useState(null);
  const [showClalImport, setShowClalImport] = useState(false);
  const [clalFiles, setClalFiles] = useState(null);
  const [showMenoraImport, setShowMenoraImport] = useState(false);
  const [menoraFiles, setMenoraFiles] = useState(null);
  const [showManagedImport, setShowManagedImport] = useState(false);
  const [managedFiles, setManagedFiles] = useState(null);
  const [showAltshulerImport, setShowAltshulerImport] = useState(false);
  const [altshulerFiles, setAltshulerFiles] = useState(null);
  const [showFilesManager, setShowFilesManager] = useState(false);
  const [showBulkSend, setShowBulkSend] = useState(false);
  const [cardSource, setCardSource] = useState({});       // { [unifiedId]: 'advice'|'holdings' }
  const [listSourceFilter, setListSourceFilter] = useState("all"); // 'all'|'advice'|'holdings'
  const [pickerSearch, setPickerSearch] = useState(""); // חיפוש בבורר הלקוח הריק
  const [portalInviteModal, setPortalInviteModal] = useState(null); // { clientName, clientTaz, url } | null
  const dragCounter = useRef(0);
  const { entries: logEntries, addEntry: addLog, updateEntry: updateLog, deleteEntry: deleteLog, reload: reloadLog } = useActivityLog();

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer.items?.length > 0) setDraggingFile(true);
  };

  const handlePortalInvite = async (item) => {
    const clientTaz  = item.clientId || item.fileInfo?.clientId;
    const clientName = item.clientName || item.fileInfo?.client || 'לקוח';
    if (!clientTaz) { alert('לא נמצא ת"ז ללקוח זה'); return; }
    try {
      const { supabase } = await import('./lib/supabase');
      const { data: { session: freshSession } } = await supabase.auth.getSession();
      const jwt = freshSession?.access_token;
      if (!jwt) throw new Error('לא מחובר');

      const res = await fetch('/api/portal/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ clientTaz, clientName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'שגיאה');
      setPortalInviteModal({ clientName, clientTaz, url: data.inviteUrl });
    } catch (err) {
      alert('שגיאה ביצירת קישור פורטל: ' + err.message);
    }
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setDraggingFile(false);
  };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDraggingFile(false);
    const files = e.dataTransfer.files;
    if (files?.length && !e.nativeEvent.__handledByChild) handleFiles(files);
  };

  const {
    clients, activeIds, setActiveIds,
    addClient, removeClient, removeDataset, toggleCompare,
    setSlot, closeSlot, openSplit,
    setActiveDataset,
    storageError
  } = useClientStore();
  const { holdingsClients, addHoldingsFunds, removeHoldingsClient, addManualFund, updateManualFund, removeFund } = useHoldingsStore();
  const { clalImports, addClalImport, removeClalImport, removeClalClient } = useClalHealthStore();
  const { imports: hachsharaImports, addImport: addHachsharaImport } = useHachsharaStore();
  const { imports: harelImports, addImport: addHarelImport } = useHarelStore();
  const { imports: menoraImports, addImport: addMenoraImport } = useMenoraStore();
  const { loadZips, status, busy, lastAdviceId, lastClientId, lastSource, reset } = useBatchLoader({ addClient, addHoldingsFunds });

  // מיגרציה חד-פעמית: הערות כלליות מהאחסון הישן → רשומות יומן (type="note").
  // רצה גם אחרי סנכרון ענן למקרה ש-snapshot ישן מחזיר את המפתח הישן.
  useEffect(() => {
    const runMigration = () => { if (migrateLegacyNotes(clients)) reloadLog(); };
    runMigration();
    window.addEventListener("pension-data-synced", runMigration);
    return () => window.removeEventListener("pension-data-synced", runMigration);
  }, [clients, reloadLog]);

  const handleFiles = (files) => {
    if (!files?.length) return;
    const all = Array.from(files);
    const isHachshara = f => /^ild_prod_1_/i.test(f.name);
    const isMigdal    = f => /^LIFE\d+\.zip$/i.test(f.name) || /^S1EUW\d+_FROMMIGDAL/i.test(f.name) || /^מגדל/i.test(f.name);
    // Harel individual files: RP*, RB*, SP*, SB* (with or without extension) or RM*.##R
    const isHarel     = f => /^(RP|RB|SP|SB)\d+(\.\w+)?$/i.test(f.name) || /^RM\d+\.\d+R(\.\w+)?$/i.test(f.name);
    // Menora files: (MM|MP) + ARJ/ZIP or any .arj file
    const isMenora    = f => /(MM|MP)\d+.*\.(arj|zip)$/i.test(f.name) || /מנורה.*\.(zip|arj)$/i.test(f.name) || /\.arj$/i.test(f.name);
    // Altshuler files
    const isAltshuler = f => /פירוט עמלות/i.test(f.name);
    // דוחות תיק מנוהל מבתי השקעות (מור/ילין) — קבצי Excel; בית ההשקעות מזוהה בתוך הפרסר
    const isManagedXls = f => /\.xlsx?$/i.test(f.name) && !isHachshara(f) && !isMigdal(f) && !isHarel(f) && !isMenora(f) && !isAltshuler(f);
    // קבצי כלל מהכספת — ארכיוני SFX (.exe) המכילים אחזקות (חיסכון) / בריאות (אפקס)
    const isClal = f => isClalExe(f);

    const hFiles  = all.filter(isHachshara);
    const mFiles  = all.filter(isMigdal);
    const rlFiles = all.filter(isHarel);
    const mnFiles = all.filter(isMenora);
    const mpFiles = all.filter(isManagedXls);
    const clFiles = all.filter(isClal);
    const alFiles = all.filter(isAltshuler);
    const regular = all.filter(f => !isHachshara(f) && !isMigdal(f) && !isHarel(f) && !isMenora(f) && !isManagedXls(f) && !isClal(f) && !isAltshuler(f));

    // ייבוא קבצי פרט (הכשרה/מגדל/הראל) ותיק מנוהל וכלל — פיצ'ר מנהל בלבד, כמו אחזקות
    if (hFiles.length || mFiles.length || rlFiles.length || mnFiles.length || mpFiles.length || clFiles.length || alFiles.length) {
      if (!hasHoldingsFeature) {
        alert("ייבוא קבצי פרט (הכשרה / מגדל / הראל) ותיק מנוהל זמין רק בחשבון עם הרשאת אחזקות. פנה למנהל המערכת.");
      } else {
        if (hFiles.length) {
          setHachsharaFiles(hFiles);
          setShowHachsharaImport(true);
        }
        if (mFiles.length) {
          setMigdalFiles(mFiles);
          setShowMigdalImport(true);
        }
        if (rlFiles.length) {
          setHarelFiles(rlFiles);
          setShowHarelImport(true);
        }
        if (mnFiles.length) {
          setMenoraFiles(mnFiles);
          setShowMenoraImport(true);
        }
        if (mpFiles.length) {
          setManagedFiles(mpFiles);
          setShowManagedImport(true);
        }
        if (clFiles.length) {
          setClalFiles(clFiles);
          setShowClalImport(true);
        }
        if (alFiles.length) {
          setAltshulerFiles(alFiles);
          setShowAltshulerImport(true);
        }
      }
    }
    if (regular.length) loadZips(regular);
  };

  // ── איחוד לקוחות טרום-ייעוץ + אחזקות לפי ת"ז ──────────────────
  const unifiedClients = useMemo(() => {
    const map = new Map(); // key = clientId (ת"ז)
    for (const a of clients) {
      const cid = a.clientId || a.fileInfo?.clientId;
      if (!cid) continue;
      map.set(cid, { advice: a, holdings: null });
    }
    for (const h of holdingsClients) {
      const cid = h.clientId;
      if (!cid) continue;
      const e = map.get(cid) || { advice: null, holdings: null };
      e.holdings = h;
      map.set(cid, e);
    }
    // כלל (אפקס) — create map entries for Clal-only clients
    if (hasHoldingsFeature) {
      for (const imp of clalImports) {
        for (const member of imp.members) {
          const cid = member.taz;
          if (!cid) continue;
          if (!map.has(cid)) map.set(cid, { advice: null, holdings: null });
        }
      }
    }
    // הכשרה — create map entries for Hachshara-only clients (same pattern as Clal)
    const hachsharaNameByTaz = new Map();
    for (const imp of hachsharaImports) {
      for (const m of imp.members) {
        if (!m.taz) continue;
        if (!map.has(m.taz)) map.set(m.taz, { advice: null, holdings: null });
        if (!hachsharaNameByTaz.has(m.taz)) {
          const fn = m.displayName || [m.firstName, m.lastName].filter(Boolean).join(' ');
          if (fn) hachsharaNameByTaz.set(m.taz, fn);
        }
      }
    }
    // הראל — same
    const harelNameByTaz = new Map();
    for (const imp of harelImports) {
      for (const m of imp.members) {
        if (!m.taz) continue;
        if (!map.has(m.taz)) map.set(m.taz, { advice: null, holdings: null });
        if (!harelNameByTaz.has(m.taz)) {
          const fn = m.displayName || [m.firstName, m.lastName].filter(Boolean).join(' ');
          if (fn) harelNameByTaz.set(m.taz, fn);
        }
      }
    }
    // מנורה — same
    const menoraNameByTaz = new Map();
    for (const imp of menoraImports) {
      for (const m of imp.members) {
        if (!m.taz) continue;
        if (!map.has(m.taz)) map.set(m.taz, { advice: null, holdings: null });
        if (!menoraNameByTaz.has(m.taz)) {
          const fn = m.displayName || [m.firstName, m.lastName].filter(Boolean).join(' ');
          if (fn) menoraNameByTaz.set(m.taz, fn);
        }
      }
    }
    // Collect Clal member names for fallback client naming
    const clalNameByTaz = new Map();
    if (hasHoldingsFeature) {
      for (const imp of clalImports) {
        for (const m of imp.members) {
          if (m.taz && m.firstName && !clalNameByTaz.has(m.taz)) clalNameByTaz.set(m.taz, m.firstName);
        }
      }
    }

    const out = [];
    for (const [cid, e] of map) {
      const { advice, holdings } = e;
      const hasAdvice = !!advice;
      const hasHoldings = !!holdings && hasHoldingsFeature;
      const id = advice ? advice.id : holdings ? holdings.id : cid;
      const def = hasHoldings ? holdings : advice;
      const addedAt =
        def?.datasets?.[def?.activeDatasetIndex]?.addedAt ||
        holdings?.updatedAt ||
        advice?.datasets?.[0]?.addedAt ||
        null;
      let clientName = advice?.clientName || holdings?.clientName || def?.fileInfo?.client || hachsharaNameByTaz.get(cid) || harelNameByTaz.get(cid) || menoraNameByTaz.get(cid) || clalNameByTaz.get(cid) || "לקוח";
      if (clientName !== "לקוח") {
        const parts = clientName.trim().split(/\s+/);
        if (parts.length > 1) {
          const firstName = parts[0];
          const lastName = parts.slice(1).join(" ");
          clientName = `${lastName} ${firstName}`;
        }
      }
      out.push({
        id,
        clientId: cid,
        clientName,
        hasAdvice,
        hasHoldings,
        advice,
        holdings,
        funds: def?.funds || [],
        // Hachshara/Harel/Clal-only clients have no advice/holdings dataset, so def
        // (and def.fileInfo) is null. Provide a synthetic fileInfo so list/search/
        // sidebar consumers that read fileInfo.client / fileInfo.clientId still work.
        fileInfo: def?.fileInfo || { client: clientName, clientId: cid },
        datasets: advice?.datasets,
        activeDatasetIndex: advice?.activeDatasetIndex,
        addedAt,
      });
    }

    // ── פרט (הכשרה/הראל/כלל) fund-card builders — shared by the latest-attach
    // loops (default view) and the monthly-history pass (month filter) below. ──
    const buildHachsharaFund = (imp, member) => {
      const totalBalance = member.funds.reduce((s, f) => s + (f.balance || 0), 0);
      return {
        id: `hachshara_${imp.companyCode}_${member.rawId}`,
        cat: "פרט",
        icon: "🏢",
        companyName: "הכשרה",
        companyCode: imp.companyCode,
        polNum: member.rawId,
        tochnit: "פרט" + (member.subProduct ? ` · ${member.subProduct}` : ""),
        joinDate: member.startDate || null,
        validDate: imp.reportDate ? imp.reportDate.split('-').reverse().join('/') : null, // DD/MM/YYYY
        redeem: totalBalance,
        prevYear: null,
        growth: null, // explicit null prevents "NaN% YTD"
        sugMutzar: 5,
        maslulim: member.funds.map(f => {
          const trackId = f.trackId || (f.name ? hachsharaTrackMap[f.name.trim()] : null);
          return {
            name: f.name, 
            balance: f.balance,
            trackId: trackId,
            code: trackId,
            sugMutzar: f.sugMutzar || (trackId ? 5 : null)
          };
        }),
        // Personal info for the dedicated פרט tab
        pratClient: {
          taz: member.taz,
          firstName: member.firstName,
          lastName: member.lastName,
          fullName: [member.firstName, member.lastName].filter(Boolean).join(" ") || null,
          city: member.city,
          gender: member.gender,
          birthDate: member.birthDate,
        },
        _sourceType: imp.sourceType || SOURCE_TYPE.PRAT,
        _hachsharaImportId: imp.id,
        _hachsharaReportDate: imp.reportDate,
        _month: imp.reportDate ? {
          period: imp.reportDate.slice(0, 7),
          valueDate: imp.reportDate,
          balance: totalBalance,
          maslulim: member.funds.map(f => {
            const trackId = f.trackId || (f.name ? hachsharaTrackMap[f.name.trim()] : null);
            return {
              name: f.name, 
              balance: f.balance,
              trackId: trackId,
              code: trackId,
              sugMutzar: f.sugMutzar || (trackId ? 5 : null)
            };
          }),
        } : null,
      };
    };

    const buildHarelFund = (imp, member) => {
      const isHealth = member.productType === 'health';
      const totalBalance = member.funds.reduce((s, f) => s + (f.balance || 0), 0);
      const totalMonthlyPremium = isHealth
        ? member.funds.reduce((s, f) => s + (f.monthlyPremium || 0), 0)
        : 0;
      const productCat =
        member.productType === 'health' ? 'בריאות' :
        member.productType === 'life'   ? 'חיים'   : 'חיסכון';
      const tochnitLabel =
        member.tochnit ||
        (member.productType === 'health' ? 'ביטוח בריאות' :
         member.productType === 'life'   ? 'ביטוח חיים'   : 'חיסכון פיננסי');
      return {
        id: `harel_${member.rawId}`,
        cat: productCat,
        icon: "🦁",
        companyName: "הראל",
        companyCode: "54",
        polNum: member.rawId,
        tochnit: tochnitLabel,
        joinDate: member.startDate || (isHealth && member.funds[0]?.startDate) || null,
        validDate: imp.reportDate ? imp.reportDate.slice(0, 7).split('-').reverse().join('/') : null, // MM/YYYY
        redeem: isHealth ? 0 : totalBalance,
        monthlyPremiumTotal: isHealth ? totalMonthlyPremium : null,
        prevYear: null,
        growth: null,
        sugMutzar: null,
        dmeiNihul: (!isHealth && (member.dmeiTzvPct != null || member.dmeiHafPct != null)) ? {
          actual: {
            dmeiTzvPct: member.dmeiTzvPct || 0,
            dmeiHafPct: member.dmeiHafPct || 0,
          }
        } : undefined,
        maslulim: member.funds.map(f => ({
          name: f.name,
          balance: f.balance,
          monthlyPremium: f.monthlyPremium || null,
          sumInsured: f.sumInsured || null,
          startDate: f.startDate || null,
          duration: f.duration || null,
          insuredTaz: f.insuredTaz || null,
          insuredName: f.insuredName || null,
          productCode: f.productCode || null,
        })),
        pratClient: {
          taz: member.taz,
          firstName: member.firstName,
          lastName:  member.lastName,
          fullName:  [member.firstName, member.lastName].filter(Boolean).join(" ") || null,
          city:      member.city,
          gender:    member.gender,
          birthDate: member.birthDate,
        },
        _sourceType: imp.sourceType || SOURCE_TYPE.PRAT,
        _harelImportId:   imp.id,
        _harelReportDate: imp.reportDate,
        _month: imp.reportDate ? {
          period: imp.reportDate.slice(0, 7),
          valueDate: imp.reportDate,
          balance: isHealth ? totalMonthlyPremium : totalBalance,
          maslulim: member.funds.map(f => ({ name: f.name, balance: f.balance })),
        } : null,
      };
    };

    const buildMenoraFund = (imp, member) => {
      const isHealth = member.productType === 'health';
      const totalBalance = member.funds.reduce((s, f) => s + (f.balance || 0), 0);
      const totalMonthlyPremium = isHealth
        ? member.funds.reduce((s, f) => s + (f.monthlyPremium || 0), 0)
        : 0;
      const productCat = isHealth ? 'בריאות' : 'פרט';
      const tochnitLabel = isHealth ? 'ביטוח בריאות' : 'חיסכון פיננסי';

      return {
        id: `menora_${imp.id}_${member.rawId}_${member.productType}`,
        cat: productCat,
        icon: "🏢",
        companyName: "מנורה",
        companyCode: "58",
        polNum: member.rawId,
        tochnit: tochnitLabel,
        joinDate: member.startDate || null,
        validDate: imp.reportDate ? imp.reportDate.slice(0, 7).split('-').reverse().join('/') : null,
        redeem: isHealth ? 0 : totalBalance,
        monthlyPremiumTotal: isHealth ? totalMonthlyPremium : null,
        prevYear: null,
        growth: null,
        sugMutzar: null,
        maslulim: member.funds.map(f => ({
          name: f.name,
          balance: f.balance || 0,
          monthlyPremium: f.monthlyPremium || null,
          startDate: f.startDate || null,
          insuredTaz: f.insuredTaz || null
        })),
        pratClient: {
          taz: member.taz,
          firstName: member.firstName,
          lastName:  member.lastName,
          fullName:  [member.firstName, member.lastName].filter(Boolean).join(" ") || null,
          birthDate: member.birthDate,
        },
        _sourceType: imp.sourceType || SOURCE_TYPE.PRAT,
        _menoraImportId: imp.id,
        _menoraReportDate: imp.reportDate,
        _month: imp.reportDate ? {
          period: imp.reportDate.slice(0, 7),
          valueDate: imp.reportDate,
          balance: isHealth ? totalMonthlyPremium : totalBalance,
          maslulim: member.funds.map(f => ({ name: f.name, balance: f.balance || 0 })),
        } : null,
      };
    };

    const buildClalFund = (imp, member) => {
      const isHealth = member.productType === "health";
      const totalMonthlyPremium = member.funds.reduce((s, f) => s + (f.monthlyPremium || 0), 0);
      return {
        id: `clal_${imp.id}_${member.rawId}`,
        cat: isHealth ? "בריאות" : "פרט",
        icon: "🔷",
        companyName: "כלל",
        companyCode: "78",
        polNum: member.rawId,
        tochnit: isHealth ? "ביטוח בריאות" : "ביטוח חיים",
        joinDate: null,
        validDate: imp.reportDate ? imp.reportDate.slice(0, 7).split('-').reverse().join('/') : null, // MM/YYYY
        redeem: 0,
        monthlyPremiumTotal: totalMonthlyPremium > 0 ? totalMonthlyPremium : null,
        baseIndex: member.baseIndex ?? null,
        collectionDay: member.collectionDay ?? null,
        prevYear: null,
        growth: null,
        sugMutzar: null,
        maslulim: member.funds.map(f => ({
          name: f.name,
          coverageType: f.coverageType || null,
          balance: 0,
          monthlyPremium: f.monthlyPremium || null,
          annualPremium: f.annualPremium || null,
          sumInsured: f.sumInsured || null,
          startDate: f.startDate || null,
          endDate: f.endDate || null,
          paymentStopDate: f.paymentStopDate || null,
          duration: f.duration || null,
          insuredTaz: f.insuredTaz || null,
          insuredName: f.insuredName || null,
          insuredRole: f.insuredRole || null,
          productCode: f.productCode || null,
        })),
        pratClient: {
          taz: member.taz,
          firstName: member.firstName,
          lastName: member.lastName,
          fullName: [member.firstName, member.lastName].filter(Boolean).join(" ") || null,
          city: member.city,
          gender: member.gender,
          birthDate: member.birthDate,
        },
        _sourceType: imp.sourceType || SOURCE_TYPE.PRAT,
        _clalImportId: imp.id,
        _clalReportDate: imp.reportDate,
        _month: imp.reportDate ? {
          period: imp.reportDate.slice(0, 7),
          valueDate: imp.reportDate,
          balance: member.funds.reduce((s, f) => s + (f.balance || 0), 0),
          maslulim: member.funds.map(f => ({ name: f.name, balance: f.balance || 0 })),
        } : null,
      };
    };

    // Find the unified client a פרט member belongs to (by linked id or ת"ז).
    const findPropClient = (member) =>
      out.find(c => (member.clientId && c.id === member.clientId) || c.clientId === member.taz);

    // Accumulate a fund into the client's per-month map (period → funds[]).
    const addMonthlyFund = (client, imp, fund) => {
      const period = imp.reportDate ? imp.reportDate.slice(0, 7) : null;
      if (!period) return;
      client.monthlyFunds = client.monthlyFunds || {};
      (client.monthlyFunds[period] = client.monthlyFunds[period] || []).push(fund);
    };

    // Attach Hachshara "פרט" funds — default view shows latest reportDate per policy.
    const hachsharaLatest = new Map();
    for (const imp of hachsharaImports) {
      for (const member of imp.members) {
        const client = findPropClient(member);
        if (!client) continue;
        const hFund = buildHachsharaFund(imp, member);
        const key = `${client.id}_${imp.companyCode}_${member.rawId}`;
        const prev = hachsharaLatest.get(key);
        if (!prev || (imp.reportDate || '') >= (prev.reportDate || '')) {
          hachsharaLatest.set(key, { hFund, client, reportDate: imp.reportDate });
        }
      }
    }
    for (const { hFund, client } of hachsharaLatest.values()) {
      client.hachsharaFunds = client.hachsharaFunds || [];
      client.hachsharaFunds.push(hFund);
    }

    // Attach Harel "פרט" funds — default view shows latest reportDate per policy.
    const harelLatest = new Map();
    for (const imp of harelImports) {
      for (const member of imp.members) {
        const client = findPropClient(member);
        if (!client) continue;
        const hFund = buildHarelFund(imp, member);
        const key = `${client.id}_${member.rawId}`;
        const prev = harelLatest.get(key);
        if (!prev || (imp.reportDate || '') >= (prev.reportDate || '')) {
          harelLatest.set(key, { hFund, client, reportDate: imp.reportDate });
        }
      }
    }
    for (const { hFund, client } of harelLatest.values()) {
      client.harelFunds = client.harelFunds || [];
      client.harelFunds.push(hFund);
    }

    // Attach Menora funds — default view shows latest reportDate per policy.
    const menoraLatest = new Map();
    for (const imp of menoraImports) {
      for (const member of imp.members) {
        const client = findPropClient(member);
        if (!client) continue;
        const hFund = buildMenoraFund(imp, member);
        const key = `${client.id}_${member.rawId}_${member.productType}`;
        const prev = menoraLatest.get(key);
        if (!prev || (imp.reportDate || '') >= (prev.reportDate || '')) {
          menoraLatest.set(key, { hFund, client, reportDate: imp.reportDate });
        }
      }
    }
    for (const { hFund, client } of menoraLatest.values()) {
      client.menoraFunds = client.menoraFunds || [];
      client.menoraFunds.push(hFund);
    }

    // Attach Clal (כלל) funds — health→בריאות, life→פרט (same shape as Harel).
    // Default view shows the latest reportDate per policy (older months live in
    // monthlyFunds for the card's month filter).
    if (hasHoldingsFeature) {
      const clalLatest = new Map();
      for (const imp of clalImports) {
        for (const member of imp.members) {
          if (!member.taz) continue;
          const client = out.find(c => c.clientId === member.taz);
          if (!client) continue;
          const key = `${client.id}_${imp.kind}_${member.rawId}`;
          const prev = clalLatest.get(key);
          if (!prev || (imp.reportDate || '') >= (prev.reportDate || '')) {
            clalLatest.set(key, { fund: buildClalFund(imp, member), client, reportDate: imp.reportDate });
          }
        }
      }
      for (const { fund, client } of clalLatest.values()) {
        client.clalFunds = client.clalFunds || [];
        client.clalFunds.push(fund);
      }
    }

    // Monthly history for פרט sources — every import is a month snapshot. Group ALL
    // uploaded months (not just the latest) so the card's month filter can show a
    // full monthly picture per source.
    for (const imp of hachsharaImports) {
      for (const member of imp.members) {
        const client = findPropClient(member);
        if (client) addMonthlyFund(client, imp, buildHachsharaFund(imp, member));
      }
    }
    for (const imp of harelImports) {
      for (const member of imp.members) {
        const client = findPropClient(member);
        if (client) addMonthlyFund(client, imp, buildHarelFund(imp, member));
      }
    }
    for (const imp of menoraImports) {
      for (const member of imp.members) {
        const client = findPropClient(member);
        if (client) addMonthlyFund(client, imp, buildMenoraFund(imp, member));
      }
    }
    if (hasHoldingsFeature) {
      for (const imp of clalImports) {
        for (const member of imp.members) {
          if (!member.taz) continue;
          const client = out.find(c => c.clientId === member.taz);
          if (client) addMonthlyFund(client, imp, buildClalFund(imp, member));
        }
      }
    }
    for (const c of out) {
      if (c.monthlyFunds) c.proprietaryMonths = Object.keys(c.monthlyFunds).sort();
    }

    // Hachshara / Harel / Clal are holdings-type sources (not pre-advice). Surface
    // them under the "אחזקות" list filter and default their card to the holdings
    // view instead of "טרום-ייעוץ".
    for (const c of out) {
      if (!c.hasHoldings && (c.hachsharaFunds?.length || c.harelFunds?.length || c.clalFunds?.length || c.menoraFunds?.length)) {
        c.hasHoldings = true;
      }
    }

    out.sort((a, b) => {
      return (a.clientName || "").localeCompare(b.clientName || "", "he-IL");
    });

    return out;
  }, [clients, holdingsClients, clalImports, hachsharaImports, harelImports, menoraImports, hasHoldingsFeature]);


  // אחרי טעינה — מנווטים ללקוח וסוגרים את המודל אחרי השהיה קצרה (כדי שהמשתמש יראה הצלחה)
  useEffect(() => {
    if (status?.type === "success" || (status?.type === "warning" && (lastAdviceId || lastClientId))) {
      const targetId = lastAdviceId || lastClientId;
      if (targetId) {
        const target = unifiedClients.find(c =>
          c.id === targetId ||
          c.clientId === targetId ||
          (c.clientId && normalizeId(c.clientId) === normalizeId(targetId))
        );
        const resolvedId = target ? target.id : targetId;
        setActiveIds([resolvedId]);
        if (lastSource && resolvedId) {
          setCardSource(prev => ({ ...prev, [resolvedId]: lastSource }));
        }
      }
      const t = setTimeout(() => {
        setShowUploadModal(false);
        reset();
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [status, lastAdviceId, lastClientId, lastSource, unifiedClients, setActiveIds, reset]);

  const visibleClients = useMemo(() => {
    if (listSourceFilter === "advice") return unifiedClients.filter(c => c.hasAdvice);
    if (listSourceFilter === "holdings") return unifiedClients.filter(c => c.hasHoldings);
    if (listSourceFilter === "retirement") {
      try {
        const raw = memoryStore.getItem("pension_plans_v1");
        const plans = raw ? JSON.parse(raw) : {};
        const clientsWithPlans = new Set(
          Object.entries(plans)
            .filter(([, v]) => Array.isArray(v) && v.length > 0)
            .map(([clientId]) => clientId)
        );
        return unifiedClients.filter(c => clientsWithPlans.has(c.clientId) || clientsWithPlans.has(c.id));
      } catch { return []; }
    }
    return unifiedClients;
  }, [unifiedClients, listSourceFilter]);

  const resolveSource = (item) => cardSource[item.id] ?? (item.hasHoldings ? "holdings" : "advice");

  const removeUnified = (item) => {
    if (!item) return;
    if (item.hasAdvice) removeClient(item.advice.id);
    if (item.hasHoldings) removeHoldingsClient(item.clientId);
    if (item.clalFunds?.length) removeClalClient(item.clientId);
    setActiveIds(prev => prev.filter(i => i !== item.id));
  };

  const dashboardItems = activeIds.map(id => {
    if (!id) return { id: `empty-${Math.random()}`, isEmpty: true };
    const item = unifiedClients.find(c =>
      c.id === id ||
      c.clientId === id ||
      (c.clientId && normalizeId(c.clientId) === normalizeId(id))
    );
    if (!item) return { id, isMissing: true };
    const source = resolveSource(item);
    const active = source === "holdings" ? item.holdings : item.advice;
    const funds = (function() {
      const holdingsFunds = active?.funds || [];

      // הפרדה מלאה: קרנות פרט (כלל/הראל/הכשרה/מנורה) שייכות למסך האחזקות בלבד.
      // בתצוגת טרום-ייעוץ (מסלקה) מציגים אך ורק קרנות 008 — בלי ערבוב מקורות.
      if (source !== "holdings") return holdingsFunds;

      const clalFunds = item.clalFunds || [];
      const harelFunds = item.harelFunds || [];
      const hachsharaFunds = item.hachsharaFunds || [];
      const menoraFunds = item.menoraFunds || [];

      const propFunds = [...clalFunds, ...harelFunds, ...hachsharaFunds, ...menoraFunds];

      if (!propFunds.length) return holdingsFunds;
      if (!holdingsFunds.length) return propFunds;

      const mergedFunds = [];
      const norm = (p) => String(p || "").replace(/^0+/, "") || "0";
      const propMap = new Map();
      for (const pf of propFunds) {
        const baseCo = (pf.companyName || "").split(" ")[0];
        propMap.set(`${baseCo}_${norm(pf.polNum)}`, pf);
      }

      for (const hf of holdingsFunds) {
        const baseCo = (hf.companyName || "").split(" ")[0];
        const key = `${baseCo}_${norm(hf.polNum)}`;
        if (propMap.has(key)) {
          propMap.delete(key); // Discard proprietary fund, keep Misleka
        }
        mergedFunds.push(hf);
      }

      mergedFunds.push(...propMap.values());
      return mergedFunds;
    })();
    // ספירת המוצרים בכותרת משקפת את המקור הנבחר (fileInfo של המסלקה עלול לשאת ספירה של מקור אחר).
    const baseFileInfo = active?.fileInfo || item.fileInfo || { client: item.clientName, clientId: item.clientId };
    return {
      ...item,
      isEmpty: false,
      source,
      funds,
      fileInfo: { ...baseFileInfo, fundsCount: funds.length },
    };
  });

  const isCompareMode = dashboardItems.length >= 2;

  if (loading) return <FullPageSpinner />;
  if (view === 'pricing') return <PricingPage onBack={() => setView(null)} />;
  if (!session) return <LoginPage onShowPricing={() => setView('pricing')} />;
  if (isExpired) return <SubscriptionGate onShowPricing={() => setView('pricing')} />;
  if (showAdmin && isAdmin) return <AdminDashboard onClose={() => setShowAdmin(false)} />;

  if (deviceBlock) {
    return (
      <DeviceBlockedModal
        reason={deviceBlock.reason}
        activeDevice={deviceBlock.activeDevice}
        devices={deviceBlock.devices}
        maxDevices={deviceBlock.maxDevices}
        onRetry={retryDevice}
        onSignOut={signOut}
      />
    );
  }

  // Wait for initial cloud sync to finish before rendering content —
  // prevents the brief flash of the empty UploadScreen while clients load from cloud.
  if (!initialSyncDone) return <FullPageSpinner message="טוען נתונים מהענן…" />;

  const storageSettingsModal = showStorageSettings && (
    <StorageSettingsModal onClose={() => setShowStorageSettings(false)} />
  );

  if (unifiedClients.length === 0) {
    if (syncStatus === 'syncing') {
      return <FullPageSpinner message="מסנכרן עם הענן…" />;
    }
    return (
      <>
        <UploadScreen onFiles={handleFiles} status={status} onOpenFilesManager={() => setShowFilesManager(true)} />
        <SignOutButton email={session?.user?.email} onSignOut={signOut} isAdmin={isAdmin} onAdmin={() => setShowAdmin(true)} onStorageSettings={() => setShowStorageSettings(true)} />
        {showFilesManager && (
          <FilesManagerModal onClose={() => setShowFilesManager(false)} />
        )}
        {storageSettingsModal}
        <PWAInstallPrompt />
      </>
    );
  }

  return (
    <div
      className="app-wrapper"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {draggingFile && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(67,56,202,0.12)",
          border: "3px dashed #4338ca",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          pointerEvents: "none",
        }}>
          <div style={{ fontSize: 64 }}>📂</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#4338ca", marginTop: 12 }}>שחרר קובץ ZIP כאן</div>
        </div>
      )}
      <div className="dashboard-area">
        {dashboardItems.length === 0 ? (
          <HomeDashboard
            clients={visibleClients}
            sourceFilter={listSourceFilter}
            onSourceFilterChange={setListSourceFilter}
            logEntries={logEntries}
            onSelectClient={id => setActiveIds([id])}
            onAddNew={() => setShowUploadModal(true)}
            onOpenLog={() => { setLogPanelClient("הכל"); setShowActivityLog(true); }}
            onUpdateLog={updateLog}
            onLogActivity={addLog}
            onDeleteLog={deleteLog}
            onOpenFilesManager={() => setShowFilesManager(true)}
            onBulkSend={() => setShowBulkSend(true)}
          />
        ) : (
          dashboardItems.map((item, idx) => (
            <div
              key={item.id}
              style={{
                flex: 1,
                minWidth: 0,
                overflowY: "auto",
                borderLeft: idx === 0 && isCompareMode ? "2px solid #e2e8f0" : "none",
                display: "flex",
                flexDirection: "column"
              }}
            >
              {item.isEmpty || item.isMissing ? (
                <div className="empty-state" style={{ background: "#f8fafc", flex: 1 }}>
                  <div style={{ fontSize: 42, marginBottom: 16 }}>👥</div>
                  <h3 style={{ margin: 0, color: "#475569" }}>בחירת לקוח לתצוגה</h3>
                  <p style={{ fontSize: 13, color: "#94a3b8", marginBottom: 20 }}>בחר לקוח מהרשימה או טען קובץ חדש</p>

                  <div style={{
                    display: "flex", flexDirection: "column", gap: 10,
                    width: "100%", maxWidth: 300, padding: 20
                  }}>
                    <input
                      type="text"
                      placeholder="🔍 שם / ת.ז..."
                      value={pickerSearch}
                      onChange={e => setPickerSearch(e.target.value)}
                      style={{
                        width: "100%", padding: "8px 12px", borderRadius: 8,
                        border: "1px solid #cbd5e1", fontSize: 14,
                        direction: "rtl", outline: "none", marginBottom: 4,
                      }}
                      autoFocus
                    />
                    {visibleClients
                      .filter(c => !activeIds.includes(c.id))
                      .filter(c => {
                        if (!pickerSearch.trim()) return true;
                        const q = pickerSearch.trim().toLowerCase();
                        const name = (c.clientName || c.fileInfo?.client || "").toLowerCase();
                        const id   = (c.clientId   || c.fileInfo?.clientId || "").toLowerCase();
                        return name.includes(q) || id.includes(q);
                      })
                      .map(c => (
                        <button
                          key={c.id}
                          onClick={() => { setSlot(idx, c.id); setPickerSearch(""); }}
                          className="btn-primary"
                          style={{ background: "white", color: "#4338ca", border: "1px solid #4338ca" }}
                        >
                          {c.clientName || c.fileInfo?.client || "לקוח ללא שם"}
                        </button>
                      ))
                    }
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="btn-primary"
                    >
                      + העלה קבצים
                    </button>
                    {isCompareMode && (
                      <button
                        onClick={() => closeSlot(idx)}
                        style={{ marginTop: 20, background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                      >
                        ✕ סגור פיצול מסך
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <ClientDashboard
                  funds={item.funds}
                  fileInfo={item.fileInfo}
                  clientId={item.id}
                  clients={unifiedClients}
                  source={item.source}
                  hasAdvice={item.hasAdvice}
                  hasHoldings={item.hasHoldings}
                  monthlyFunds={item.monthlyFunds}
                  proprietaryMonths={item.proprietaryMonths}
                  onToggleSource={(src) => setCardSource(prev => ({ ...prev, [item.id]: src }))}
                  onLoadNew={() => setShowUploadModal(true)}
                  onRemove={() => removeUnified(item)}
                  isCompareMode={isCompareMode}
                  onOpenSplit={openSplit}
                  onCloseSplit={() => closeSlot(idx)}
                  onSetSlot={(id) => setSlot(idx, id)}
                  onLogActivity={addLog}
                  logEntries={logEntries}
                  onUpdateLog={updateLog}
                  onDeleteLog={deleteLog}
                  onSelectClient={(id) => setActiveIds([id])}
                  onSwitchDataset={setActiveDataset}
                  onSendPortalInvite={() => handlePortalInvite(item)}
                  onAddManualFund={addManualFund}
                  onUpdateManualFund={updateManualFund}
                  onRemoveFund={removeFund}
                  onGoHome={() => setActiveIds([])}
                  onOpenLog={() => {
                    setLogPanelClient(item.fileInfo?.client || 'הכל');
                    setShowActivityLog(true);
                  }}
                />
              )}
            </div>
          ))
        )}
      </div>

      {dashboardItems.length > 0 && !isMobile && (
        <ClientSidebar
          clients={visibleClients}
          sourceFilter={listSourceFilter}
          onSourceFilterChange={setListSourceFilter}
          activeIds={activeIds}
          onSelect={(id) => setActiveIds([id])}
          onToggleCompare={toggleCompare}
          onRemove={(id) => removeUnified(unifiedClients.find(c => c.id === id))}
          onAddNew={() => setShowUploadModal(true)}
          onImport={() => setShowUploadModal(true)}
          onSwitchDataset={setActiveDataset}
          onRemoveDataset={removeDataset}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(p => !p)}
          onSignOut={signOut}
          onStorageSettings={() => setShowStorageSettings(true)}
          onAdmin={() => setShowAdmin(true)}
          isAdmin={isAdmin}
          email={session?.user?.email}
          onOpenLog={() => {
            const activeName = dashboardItems[0]?.fileInfo?.client || "הכל";
            setLogPanelClient(activeName);
            setShowActivityLog(true);
          }}
          onGoHome={() => setActiveIds([])}
          onOpenFilesManager={() => setShowFilesManager(true)}
          onBulkSend={() => setShowBulkSend(true)}
        />
      )}

      {/* Mobile: hamburger button + drawer */}
      {dashboardItems.length > 0 && isMobile && (
        <>
          <button
            onClick={() => setMobileMenuOpen(true)}
            style={{
              position: "fixed", top: 12, right: 12, zIndex: 190,
              width: 44, height: 44, borderRadius: 12,
              background: "white", border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              fontSize: 20, cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center",
            }}
            title="תפריט"
          >☰</button>
          <MobileDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
            <ClientSidebar
              clients={clients}
              activeIds={activeIds}
              onSelect={(id) => { setActiveIds([id]); setMobileMenuOpen(false); }}
              onToggleCompare={toggleCompare}
              onRemove={removeClient}
              onAddNew={() => { setShowUploadModal(true); setMobileMenuOpen(false); }}
              onSwitchDataset={setActiveDataset}
              collapsed={false}
              onToggleCollapse={() => {}}
              onSignOut={signOut}
              onStorageSettings={() => { setShowStorageSettings(true); setMobileMenuOpen(false); }}
              onAdmin={() => { setShowAdmin(true); setMobileMenuOpen(false); }}
              isAdmin={isAdmin}
              email={session?.user?.email}
              onOpenLog={() => {
                const activeName = dashboardItems[0]?.fileInfo?.client || "הכל";
                setLogPanelClient(activeName);
                setShowActivityLog(true);
                setMobileMenuOpen(false);
              }}
              onGoHome={() => { setActiveIds([]); setMobileMenuOpen(false); }}
              onOpenFilesManager={() => { setShowFilesManager(true); setMobileMenuOpen(false); }}
              onBulkSend={() => { setShowBulkSend(true); setMobileMenuOpen(false); }}
            />
          </MobileDrawer>
        </>
      )}

      {showUploadModal && (
        <UploadModal
          onFiles={handleFiles}
          status={status}
          busy={busy}
          onClose={() => setShowUploadModal(false)}
          onManagedImport={hasHoldingsFeature ? () => { setShowUploadModal(false); setShowManagedImport(true); } : undefined}
        />
      )}

      {showBulkSend && (
        <BulkReportSendModal
          clients={unifiedClients}
          onClose={() => setShowBulkSend(false)}
          onLogActivity={addLog}
        />
      )}

      {showHachsharaImport && (
        <HachsharaImport
          initialFiles={hachsharaFiles}
          existingClients={unifiedClients}
          addImport={addHachsharaImport}
          addClient={addClient}
          onClose={() => { setShowHachsharaImport(false); setHachsharaFiles(null); }}
        />
      )}

      {showMigdalImport && (
        <MigdalImport
          files={migdalFiles}
          existingClients={unifiedClients}
          onClose={() => { setShowMigdalImport(false); setMigdalFiles(null); }}
        />
      )}

      {showHarelImport && (
        <HarelImport
          initialFiles={harelFiles}
          existingClients={unifiedClients}
          addImport={addHarelImport}
          addClient={addClient}
          onClose={() => { setShowHarelImport(false); setHarelFiles(null); }}
        />
      )}

      {showClalImport && (
        <ClalImport
          initialFiles={clalFiles}
          existingClients={unifiedClients}
          addImport={addClalImport}
          onClose={() => { setShowClalImport(false); setClalFiles(null); }}
        />
      )}
      {showMenoraImport && (
        <MenoraImport
          initialFiles={menoraFiles}
          existingClients={unifiedClients}
          addImport={addMenoraImport}
          onClose={() => { setShowMenoraImport(false); setMenoraFiles(null); }}
        />
      )}

      {showManagedImport && (
        <ManagedPortfolioImport
          initialFiles={managedFiles}
          holdingsClients={holdingsClients}
          addHoldingsFunds={addHoldingsFunds}
          onClose={() => { setShowManagedImport(false); setManagedFiles(null); }}
        />
      )}

      {showAltshulerImport && (
        <AltshulerImport
          files={altshulerFiles}
          clients={unifiedClients}
          holdingsClients={holdingsClients}
          onImport={addHoldingsFunds}
          onClose={() => { setShowAltshulerImport(false); setAltshulerFiles(null); }}
        />
      )}

      {showFilesManager && (
        <FilesManagerModal
          onClose={() => setShowFilesManager(false)}
          onOpenUpload={() => { setShowFilesManager(false); setShowUploadModal(true); }}
        />
      )}

      {showActivityLog && (
        <ActivityLogPanel
          entries={logEntries}
          onAdd={addLog}
          onUpdate={updateLog}
          onDelete={deleteLog}
          clients={unifiedClients}
          defaultClient={logPanelClient}
          onClose={() => { setShowActivityLog(false); setLogPanelClient("הכל"); }}
        />
      )}


      {status?.type === "loading" && (
        <div className="floating-alert loading">
          ⏳ {status.msg}
        </div>
      )}

      {status?.type === "error" && (
        <div className="floating-alert error">
          ❌ {status.msg}
        </div>
      )}

      {storageError && (
        <div className="floating-alert warning bottom">
          ⚠️ {storageError}
        </div>
      )}

      {/* Portal Invite Modal */}
      {portalInviteModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'white', borderRadius: 20, padding: '36px 32px', maxWidth: 480,
            width: '90%', direction: 'rtl', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: '0 0 6px' }}>
              📤 קישור פורטל ללקוח
            </h2>
            <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 20px' }}>
              שלח ללקוח <strong>{portalInviteModal.clientName}</strong> את הקישור הבא להרשמה חד-פעמית לפורטל:
            </p>
            <div style={{
              background: '#f1f5f9', borderRadius: 10, padding: '12px 16px',
              fontFamily: 'monospace', fontSize: 13, color: '#1e3a5f',
              wordBreak: 'break-all', marginBottom: 16,
            }}>
              {portalInviteModal.url}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={() => { navigator.clipboard.writeText(portalInviteModal.url); }}
                style={{
                  flex: 1, padding: '10px', borderRadius: 10, border: 'none',
                  background: '#4338ca', color: 'white', cursor: 'pointer', fontWeight: 700, fontSize: 14,
                }}
              >📋 העתק קישור</button>
              <button
                onClick={() => setPortalInviteModal(null)}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0',
                  background: 'white', color: '#475569', cursor: 'pointer', fontWeight: 600, fontSize: 14,
                }}
              >סגור</button>
            </div>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 14, marginBottom: 0 }}>
              ⏳ הקישור בתוקף ל-7 ימים. אחרי הרשמה, הלקוח יוכל להתחבר עם מייל וסיסמה.
            </p>
          </div>
        </div>
      )}

      {storageSettingsModal}
      <PWAInstallPrompt />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
